import { useCallback, useState } from 'react';
import { company } from '../data/company';

/**
 * Drives a form's idle -> submitting -> success/error cycle.
 *
 * Deliberately never fakes success: if the backend is unreachable or not yet
 * configured, the user is told so and offered the mailto: fallback, because a
 * green tick over a lead that went nowhere is worse than an honest error.
 */
export function useFormSubmit(submitFn) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  // 'api' when the backend accepted it, 'mailto' when we handed the message to
  // the visitor's mail client instead. The two deserve different wording.
  const [channel, setChannel] = useState(null);

  const submit = useCallback(
    async (...args) => {
      setStatus('submitting');
      setError(null);
      try {
        const via = await submitFn(...args);
        setChannel(via === 'mailto' ? 'mailto' : 'api');
        setStatus('success');
        return true;
      } catch (err) {
        setStatus('error');
        setError(
          err?.code === 'NOT_CONFIGURED' || err?.code === 'NETWORK'
            ? `We couldn’t submit that just now — please email us at ${company.email}.`
            : err?.message ?? 'Something went wrong.',
        );
        if (process.env.NODE_ENV === 'development') console.error(err);
        return false;
      }
    },
    [submitFn],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setChannel(null);
  }, []);

  return { status, error, channel, submit, reset, isSubmitting: status === 'submitting' };
}
